import React, { createContext, useState, useContext, useEffect } from "react";
import { authAPI } from "../services/api";
import { tr } from "../i18n";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
    // Runs when the token changes, not when verifyToken is re-created each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await authAPI.verify();

      // A platform account belongs in the super-admin app; a leftover session
      // from before that rule is dropped rather than shown a shop
      if (response.data.user?.role === "super_admin") {
        logout();
        return;
      }

      setUser(response.data.user);
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (identifier, password) => {
    try {
      const response = await authAPI.login(identifier, password);
      const { user, token } = response.data;

      // The API refuses these accounts on every shop route anyway; this just
      // says why at the door instead of signing in to a screen of errors
      if (user?.role === "super_admin") {
        return { success: false, error: tr("auth.login.platformAccount") };
      }

      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));

      setToken(token);
      setUser(user);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || tr("auth.login.failed"),
      };
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setToken(null);
    setUser(null);
  };

  const isAdmin = () => user?.role === "admin";

  /** Merges a self-edit (name, phone, …) into the signed-in user everywhere it's shown. */
  const updateUser = (changes) => {
    setUser((current) => {
      const next = { ...current, ...changes };
      localStorage.setItem("user", JSON.stringify(next));
      return next;
    });
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, logout, isAdmin, token, updateUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
